/* eslint-disable */
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { useDispatch, useSelector } from 'react-redux';
import {
	Modal,
	Box,
	Typography,
	IconButton,
	Button,
	Paper
} from '@mui/material';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { formatDate } from '@/Helpers';
import { modalActions } from '@/Actions';
import { ChevronLeft, ChevronRight, Download, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';


const modalStyle = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '90vw',
	maxWidth: '1000px',
	height: '90vh',
	maxHeight: '700px',
	bgcolor: 'background.paper',
	borderRadius: 2,
	boxShadow: 24,
	outline: 'none',
	display: 'flex',
	flexDirection: 'column'
};

export function DocumentModal() {
	const defaultLayoutPluginInstance = defaultLayoutPlugin();
	const docData = useSelector((state) => state?.modal?.documentModal);
	const dispatch = useDispatch();
	const { show, data = {} } = docData;
	const { obj = {}, list = [] } = data;
	const [currentDoc, setCurrentDoc] = useState({});
	const [currentIdx, setCurrentIdx] = useState(0);

	useEffect(() => {
		// Only update if obj exists and has meaningful data
		if (obj && Object.keys(obj).length > 0 && obj !== currentDoc) {
			setCurrentDoc(obj);
		}
	}, [obj?.id, obj?.name, obj?.base64url, obj?.uploadedDate]); // Use specific properties as dependencies

	const handleClose = () => {
		dispatch(modalActions?.toggleDocumentModal(false));
	};

	const handleDocNavigation = (idx, type) => {
		if (type === 'prev') {
			setCurrentDoc(list[idx - 1]);
			setCurrentIdx(idx - 1);
		} else {
			setCurrentDoc(list[idx + 1]);
			setCurrentIdx(idx + 1);
		}
	}

	const handleDeleteDoc = async (id) => {
		if (data?.handleDelete) {
			data?.handleDelete(id);
		}
	}

	return (
		<Modal
			open={show}
			onClose={handleClose}
			aria-labelledby="document-modal-title"
			disableEscapeKeyDown
			disableBackdropClick
		>
			<Box sx={modalStyle}>
				{/* Header */}
				<Box sx={{
					p: 3,
					borderBottom: '1px solid #e0e0e0',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'flex-start'
				}}>
					<Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
							<Typography
								id="document-modal-title"
								variant="h6"
								component="h2"
								sx={{ color: '#111827' }}
							>
								{currentDoc?.name}
							</Typography>
							{currentDoc?.type && (
								<Box sx={{
									backgroundColor: '#eff6ff',
									color: '#2563eb',
									fontSize: '10px',
									fontWeight: 700,
									px: 1,
									py: 0.25,
									borderRadius: 0.5,
									border: '1px solid #dbeafe',
									textTransform: 'uppercase',
									letterSpacing: '0.025em'
								}}>
									{currentDoc.type}
								</Box>
							)}
						</Box>
						<Typography
							variant="body2"
							sx={{ color: '#2563eb' }}
						>
							Uploaded: {currentDoc?.uploadedDate}
						</Typography>
					</Box>
					{currentDoc?.podId && (
						<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', px: 1.25, py: 0.5, borderRadius: 9999 }}>
							<Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600 }}>POD ID</Typography>
							<Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 500 }}>{currentDoc?.podId}</Typography>
						</Box>
					)}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{currentDoc?.delete && (
							<Trash2 size={20} className='cursor-pointer text-red-600' onClick={() => handleDeleteDoc(currentDoc?.id)} />
						)}
						{currentIdx > 0 && <ChevronLeft size={24} className='cursor-pointer' onClick={() => handleDocNavigation(currentIdx, 'prev')} />}
						{currentIdx < list?.length - 1 && <ChevronRight size={24} className='cursor-pointer' onClick={() => handleDocNavigation(currentIdx, 'next')} />}
						<IconButton
							onClick={handleClose}
							sx={{
								color: '#6b7280',
								'&:hover': {
									color: '#374151'
								}
							}}
						>
							<X size={24} />
						</IconButton>
					</Box>
				</Box>

				{/* PDF Viewer */}
				<Box sx={{
					flex: 1,
					overflow: 'hidden'
				}}>
					<Paper
						elevation={1}
						sx={{
							height: '100%',
							border: '1px solid #e5e7eb',
							overflow: 'hidden'
						}}
					>
						<Worker workerUrl='https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js'>
							<Viewer
								fileUrl={currentDoc?.base64url}
								plugins={[defaultLayoutPluginInstance]}
							/>
						</Worker>
					</Paper>
				</Box>
			</Box>
		</Modal>
	);
}